import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/lib/base/badges/badges';
import { resolveSlots } from '@/lib/scheduling/slotResolve';

interface MeetingSlotBadgeProps {
  slotId: string;
  orgId: number;
  /**
   * When provided, the badge renders this label immediately with no network
   * request — the query is disabled entirely. Used when the caller already
   * has the resolved slot from the action's `enriched_data.resolved_slots`.
   * `label` is server-rendered (see slotResolve.ts) and displayed verbatim.
   */
  preResolved?: { start: string; end: string; label: string };
}

export function MeetingSlotBadge({
  slotId,
  orgId,
  preResolved,
}: MeetingSlotBadgeProps) {
  // Hook must run unconditionally (before any early return) even when
  // preResolved makes the query unnecessary — `enabled: false` skips the
  // network request while keeping hook order stable.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['scheduling', orgId, 'slot-resolve', slotId],
    queryFn: async () => {
      const resolved = await resolveSlots(orgId, [slotId]);
      return resolved.find(s => s.id === slotId) ?? null;
    },
    enabled: !preResolved,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  if (preResolved) {
    return (
      <span className='inline-flex align-middle'>
        <Badge type='pill-color' color='brand'>
          {preResolved.label}
        </Badge>
      </span>
    );
  }

  if (isLoading) {
    return (
      <span className='inline-flex align-middle'>
        <Badge type='pill-color' color='brand'>
          …
        </Badge>
      </span>
    );
  }

  if (isError || !data) {
    return (
      <span className='inline-flex align-middle'>
        <Badge type='pill-color' color='gray'>
          Meeting time
        </Badge>
      </span>
    );
  }

  return (
    <span className='inline-flex align-middle'>
      <Badge type='pill-color' color='brand'>
        {data.label}
      </Badge>
    </span>
  );
}
